import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationsEndpointIdentifier } from './use-get-source-brand-locations-api';

const endpoint = 'ums/v1/crm/management/source-brand-locations';
export const setParentSourceBrandLocationEndpointIdentifier = endpoint;

interface MutationPayload {
  id: number;
  parentId: number | null;
}

type TData = {
  message: string;
};
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, MutationPayload>;
type ReturnType = UseMutationResult<TData, TError, MutationPayload>;

export function useSetParentSourceBrandLocationApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async ({ id, parentId }: MutationPayload) => {
    try {
      const response = await http.patch(`${endpoint}/${id}/parent`, { parentId });

      toast({
        description: 'Parent location set successfully',
        status: 'success',
      });

      return response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: (_, variables) => {
      // Invalidate main list queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationsEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });

      // Invalidate children queries since parent-child relationships changed
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, type] = query.queryKey;
          return queryEndpoint === getSourceBrandLocationsEndpointIdentifier && type === 'children';
        },
      });

      // Invalidate specific location details
      queryClient.invalidateQueries([getSourceBrandLocationsEndpointIdentifier, 'details', variables.id]);
    },
    ...otherOptions,
  });
}
